import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild, inject, DestroyRef, OnInit } from '@angular/core';
import { PopoverComponent } from '../popover/popover.component';
import { DateFormattingService } from '../../../app/services/date-formatting.service';
import { Calendar, ChevronLeft, ChevronRight, XCircle } from 'lucide-angular';
import { PosthogService } from '../../../app/services/posthog.service';
import { LocalStorageService } from '../../../app/services/local-storage.service';
import { UserModel } from '../../../app/models';
import { EntityService } from '../../../app/services/entity.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'mos-date-picker',
	templateUrl: './date-picker.component.html',
})
export class DatePickerComponent implements AfterViewInit, OnChanges, OnInit {
	@ViewChild('monthPopover') public monthPopover!: PopoverComponent;
	@ViewChild('yearPopover') public yearPopover!: PopoverComponent;
	@ViewChild('datePickerInput') public datePickerInput!: ElementRef;
	@ViewChild('datePickerDropdown') public datePickerDropdown!: ElementRef;
	@Input() clearable: boolean = false;
	@Input() disabled: boolean = false;
	@Input() singleDateInput: Date;
	@Input() dateRangeInput: { startDate: Date; endDate: Date };
	@Input() singleDate: boolean = false;
	@Input() dateRange: boolean = true;
	@Input() clearInput: boolean = false;
	@Input() showAsButton: boolean = false;
	@Input() showPresets: boolean = false;
	@Output() selectedDate: EventEmitter<Date | null> = new EventEmitter<Date | null>();
	@Output() selectedRange: EventEmitter<{ from: Date | null; to: Date | null }> = new EventEmitter<{ from: Date | null; to: Date | null }>();
	@Output() inputCleared: EventEmitter<void> = new EventEmitter<void>();

	public isOpen = false;
	public currentUser: UserModel;
	public extendedAdvancedSearchIsEnabled: boolean = false;
	public selectedSingleDate: Date | null = null;
	public selectedRangeValue: { from: Date | null; to: Date | null } = { from: null, to: null };
	public months = Array.from({ length: 12 }, (_, i) => i);
	public yearRange = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() + 6 - i);
	public month: number = new Date().getMonth();
	public year: number = new Date().getFullYear();
	public daysOfWeek = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
	public daysCache: { day: number; empty: boolean }[] | null = null;
	public dateRangeString: string = '';
	public position: 'bottom' | 'top' = 'bottom';
	public firstDayOfTheWeek: number = 1;
	public datePickerOpen = false;

	private destroyRef = inject(DestroyRef);

	constructor(
		private dateFormattingService: DateFormattingService,
		private localStorageService: LocalStorageService,
		private posthogService: PosthogService,
		private entityService: EntityService
	) {}

	public ngOnInit() {
		this.currentUser = this.localStorageService.getUserDetails();
		this.extendedAdvancedSearchIsEnabled = this.posthogService.isEnabled('project_search_filters_update');

		this.entityService
			.getCurrentEntity()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(entity => {
				this.firstDayOfTheWeek = entity.calendarStartDay ?? 1;
				this.updateDaysOfWeekLabels();
				this.clearDaysCache();
			});
	}

	@HostListener('document:click', ['$event'])
	handleClickOutside(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (this.isOpen && !target.closest('.date-picker-container')) {
			this.isOpen = false;
		}
	}

	ngAfterViewInit() {
		this.setInputDates();
	}

	public ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges.clearInput && this.clearInput === true) {
			this.clearDate();
		}

		if (simpleChanges.singleDateInput) {
			if (this.singleDateInput === null || this.singleDateInput === undefined) {
				this.clearDate();
			} else {
				this.setInputDates();
			}
		}

		if (simpleChanges.dateRangeInput) {
			if (this.dateRangeInput === null || this.dateRangeInput === undefined) {
				this.clearDate();
			} else {
				this.setInputDates();
			}
		}
	}

	/**
	 * Updates the labels for the days of the week based on the firstDayOfTheWeek setting
	 * @private
	 */
	private updateDaysOfWeekLabels() {
		const allDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

		this.daysOfWeek = [...allDays.slice(this.firstDayOfTheWeek), ...allDays.slice(0, this.firstDayOfTheWeek)];
	}

	public toggleDatePicker() {
		this.datePickerOpen = !this.datePickerOpen;
		if (this.datePickerOpen) {
			//wait for dom to render datepicker
			setTimeout(() => {
				this.adjustPosition();
			}, 0);
		}
	}

	public adjustPosition() {
		if (!this.datePickerInput || !this.datePickerDropdown) return;

		const inputRect = this.datePickerInput.nativeElement.getBoundingClientRect();
		const dropdown = this.datePickerDropdown.nativeElement;
		const viewportHeight = window.innerHeight;

		const spaceBelow = viewportHeight - inputRect.bottom;
		const spaceAbove = inputRect.top;

		this.position = spaceBelow > dropdown.offsetHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';
	}

	private setInputDates() {
		if (this.singleDate && this.singleDateInput) {
			this.selectedSingleDate = new Date(this.singleDateInput);
			let day: number = this.selectedSingleDate.getDate();
			this.year = this.selectedSingleDate.getFullYear();
			this.month = this.selectedSingleDate.getMonth();
			this.isSelected(day);
		}
		if (this.dateRange && this.dateRangeInput) {
			this.selectedRangeValue.from = new Date(this.dateRangeInput.startDate);
			this.selectedRangeValue.to = new Date(this.dateRangeInput.endDate);
		}
		this.getDateRangeString();
	}

	public prevMonth(event: Event) {
		event.stopPropagation();
		if (this.month === 0) {
			this.month = 11;
			this.year--;
		} else {
			this.month--;
		}
		this.clearDaysCache();
	}

	public nextMonth(event: Event) {
		event.stopPropagation();
		if (this.month === 11) {
			this.month = 0;
			this.year++;
		} else {
			this.month++;
		}
		this.clearDaysCache();
	}

	// Cache the result of getDaysInMonth to avoid recalculations
	public getDaysInMonth(): { day: number; empty: boolean }[] {
		if (this.daysCache) {
			return this.daysCache;
		}

		const firstDay = new Date(this.year, this.month, 1).getDay();
		const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();

		// Calculate the offset based on firstDayOfTheWeek
		const startDay = (firstDay - this.firstDayOfTheWeek + 7) % 7;
		const daysArray = [];

		for (let i = 0; i < startDay; i++) {
			daysArray.push({ day: null, empty: true });
		}

		for (let i = 1; i <= daysInMonth; i++) {
			daysArray.push({ day: i, empty: false });
		}

		this.daysCache = daysArray;
		return daysArray;
	}

	public selectThisMonth() {
		if (this.singleDate) {
			this.selectedSingleDate = new Date(this.year, this.month, 1);
			this.getDateRangeString();
			this.selectedDate.emit(this.selectedSingleDate);
			this.datePickerOpen = false;
			return;
		}

		const from = new Date(this.year, this.month, 1);
		const to = new Date(this.year, this.month + 1, 0);

		this.selectedRangeValue = { from, to };
		this.getDateRangeString();
		this.selectedRange.emit(this.selectedRangeValue);
		this.datePickerOpen = false;
	}

	public selectThisYear() {
		if (this.singleDate) {
			this.selectedSingleDate = new Date(this.year, 0, 1);
			this.getDateRangeString();
			this.selectedDate.emit(this.selectedSingleDate);
			this.datePickerOpen = false;
			return;
		}

		const from = new Date(this.year, 0, 1);
		const to = new Date(this.year, 11, 31);

		this.selectedRangeValue = { from, to };
		this.getDateRangeString();
		this.selectedRange.emit(this.selectedRangeValue);
		this.datePickerOpen = false;
	}

	public selectLastMonth() {
		let prevMonth = this.month - 1;
		let year = this.year;

		if (prevMonth < 0) {
			prevMonth = 11;
			year--;
		}

		const from = new Date(year, prevMonth, 1);
		const to = new Date(year, prevMonth + 1, 0);

		this.selectedRangeValue = { from, to };
		this.getDateRangeString();
		this.selectedRange.emit(this.selectedRangeValue);
		this.datePickerOpen = false;
	}

	public selectThisFinancialYear() {
		const startMonth = this.currentUser?.entity?.fiscalMonthStart ?? 6;
		let from: Date, to: Date;

		if (this.month >= startMonth) {
			from = new Date(this.year, startMonth, 1);
			to = new Date(this.year + 1, startMonth, 0);
		} else {
			from = new Date(this.year - 1, startMonth, 1);
			to = new Date(this.year, startMonth, 0);
		}

		if (this.singleDate) {
			this.selectedSingleDate = from;
			this.getDateRangeString();
			this.selectedDate.emit(this.selectedSingleDate);
			this.datePickerOpen = false;
			return;
		}

		this.selectedRangeValue = { from, to };
		this.getDateRangeString();
		this.selectedRange.emit(this.selectedRangeValue);
		this.datePickerOpen = false;
	}

	public selectToday() {
		const today = new Date();

		if (this.singleDate) {
			this.selectedSingleDate = today;
			this.getDateRangeString();
			this.selectedDate.emit(this.selectedSingleDate);
			this.datePickerOpen = false;
			return;
		}

		this.selectedRangeValue = { from: today, to: today };
		this.getDateRangeString();
		this.selectedRange.emit(this.selectedRangeValue);
		this.datePickerOpen = false;
	}

	public clearDaysCache() {
		this.daysCache = null;
	}

	public selectDate(day: number, event: Event) {
		const selected = new Date(this.year, this.month, day);

		if (this.singleDate) {
			this.selectedSingleDate = selected;
			this.selectedDate.emit(selected);
			event.stopPropagation();
			this.datePickerOpen = false;
		} else {
			if (!this.selectedRangeValue.from || this.selectedRangeValue.to) {
				this.selectedRangeValue = { from: selected, to: null };
			} else {
				if (selected < this.selectedRangeValue.from) {
					this.selectedRangeValue = { from: selected, to: null };
				} else {
					this.selectedRangeValue.to = selected;
					this.datePickerOpen = false;
					this.selectedRange.emit(this.selectedRangeValue);
				}
			}
		}
		this.getDateRangeString();
	}

	public isSelected(day: number): boolean {
		const current = new Date(this.year, this.month, day).getTime();

		if (this.singleDate && this.selectedSingleDate) {
			return current === this.selectedSingleDate.getTime();
		}

		if (this.selectedRangeValue.from && this.selectedRangeValue.to) {
			return current === this.selectedRangeValue.from.getTime() || current === this.selectedRangeValue.to.getTime();
		}

		return this.selectedRangeValue.from && current === this.selectedRangeValue.from.getTime();
	}

	public isInRange(day: number): boolean {
		const current = new Date(this.year, this.month, day).getTime();
		if (this.selectedRangeValue.from && this.selectedRangeValue.to) {
			const from = this.selectedRangeValue.from.getTime();
			const to = this.selectedRangeValue.to.getTime();
			return current > from && current < to;
		}
		return false;
	}

	public getDateRangeString() {
		if (this.selectedRangeValue.from && this.selectedRangeValue.to) {
			const from: string = this.dateFormattingService.formatDateString(this.selectedRangeValue.from);
			const to: string = this.dateFormattingService.formatDateString(this.selectedRangeValue.to);
			if (from && to) {
				this.dateRangeString = `${from} - ${to}`;
			} else {
				this.dateRangeString = '';
			}
		} else if (this.selectedSingleDate) {
			this.dateRangeString = this.dateFormattingService.formatDateString(this.selectedSingleDate);
		} else {
			this.dateRangeString = '';
		}
	}

	public getMonthName(month: number): string {
		return new Date(0, month).toLocaleString('default', { month: 'long' });
	}

	public setMonth(month: number) {
		this.month = month;
		this.monthPopover.close();
		this.clearDaysCache();
	}

	public setYear(year: number) {
		this.year = year;
		this.yearPopover.close();
		this.clearDaysCache();
	}

	public openMonthPopover(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();
		this.monthPopover.open(event);
	}

	public openYearPopover(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();
		this.yearPopover.open(event);
	}

	public clearDate() {
		this.selectedRangeValue = { from: undefined, to: undefined };
		this.selectedSingleDate = undefined;
		this.dateRangeString = '';
		this.inputCleared.emit();
	}

	public closeDatePicker(): void {
		this.datePickerOpen = false;
	}

	protected readonly XCircle = XCircle;
	protected readonly Calendar = Calendar;
	protected readonly ChevronLeft = ChevronLeft;
	protected readonly ChevronRight = ChevronRight;
}
